/**
 * Деньги — только целые минорные единицы (копейки/центы).
 * Курс — целое в миллионных долях (ppm): rate_ppm = rate * 1_000_000.
 * Деление допускается только при форматировании.
 */
export const RATE_SCALE = 1000000n;
export const RATE_ONE_PPM = 1_000_000;
export const CURRENCY_CODES = ['BYN', 'USD'];
export function isCurrencyCode(value) {
    return value === 'BYN' || value === 'USD';
}
export const CURRENCIES = {
    BYN: { code: 'BYN', nbrbCurId: null, symbol: 'Br', minorUnits: 2 },
    // Актуальная запись USD в НБРБ — 431 (145 закрыта 2021-07-08).
    USD: { code: 'USD', nbrbCurId: 431, symbol: '$', minorUnits: 2 },
};
export function money(amountMinor, currency) {
    return { amountMinor, currency };
}
/** Округление к ближайшему, half-up по модулю, на bigint. */
function divRound(numerator, denominator) {
    if (denominator === 0n)
        throw new Error('divRound: denominator is zero');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const q = (n * 2n + d) / (d * 2n);
    return negative ? -q : q;
}
/**
 * Курс НБРБ вида «Cur_Scale единиц валюты = Cur_OfficialRate BYN»
 * приводится к ppm за одну единицу: 1 quote = rate_ppm / 1e6 BYN.
 */
export function nbrbRateToPpm(officialRate, curScale) {
    if (!Number.isFinite(officialRate) || officialRate <= 0) {
        throw new Error(`nbrbRateToPpm: bad rate ${officialRate}`);
    }
    if (!Number.isInteger(curScale) || curScale <= 0) {
        throw new Error(`nbrbRateToPpm: bad scale ${curScale}`);
    }
    // officialRate приходит с 4 знаками после запятой.
    const rateMicro = Math.round(officialRate * 1e6);
    return Number(divRound(BigInt(rateMicro), BigInt(curScale)));
}
/**
 * Конвертация суммы из валюты в базовую по зафиксированному ppm-курсу.
 * ratePpm — курс `from → base`, т.е. 1 from = ratePpm/1e6 base.
 */
export function convertMinor(amountMinor, ratePpm) {
    if (!Number.isInteger(amountMinor)) {
        throw new Error(`convertMinor: amountMinor must be integer, got ${amountMinor}`);
    }
    if (!Number.isInteger(ratePpm) || ratePpm <= 0) {
        throw new Error(`convertMinor: ratePpm must be positive integer, got ${ratePpm}`);
    }
    return Number(divRound(BigInt(amountMinor) * BigInt(ratePpm), RATE_SCALE));
}
/**
 * Курс `from → to`, когда обе валюты выражены через BYN.
 * ppm валюты BYN равен RATE_ONE_PPM.
 */
export function crossRatePpm(fromPpmToByn, toPpmToByn) {
    if (!Number.isInteger(fromPpmToByn) || fromPpmToByn <= 0) {
        throw new Error(`crossRatePpm: bad from ${fromPpmToByn}`);
    }
    if (!Number.isInteger(toPpmToByn) || toPpmToByn <= 0) {
        throw new Error(`crossRatePpm: bad to ${toPpmToByn}`);
    }
    return Number(divRound(BigInt(fromPpmToByn) * RATE_SCALE, BigInt(toPpmToByn)));
}
/** Строка «12.50» из минорных единиц, без символа валюты. */
export function formatMinor(amountMinor, currency) {
    const minorUnits = CURRENCIES[currency].minorUnits;
    const factor = 10 ** minorUnits;
    const negative = amountMinor < 0;
    const abs = Math.abs(amountMinor);
    const whole = Math.trunc(abs / factor);
    const frac = abs % factor;
    const fracStr = minorUnits > 0 ? `.${String(frac).padStart(minorUnits, '0')}` : '';
    return `${negative ? '-' : ''}${whole}${fracStr}`;
}
export function formatMoney(m) {
    return `${formatMinor(m.amountMinor, m.currency)} ${CURRENCIES[m.currency].symbol}`;
}
/** Разбор пользовательского ввода «12,50» / «12.5» / «12» в минорные единицы. */
export function parseAmountToMinor(input, currency) {
    const minorUnits = CURRENCIES[currency].minorUnits;
    const normalized = input.trim().replace(',', '.').replace(/\s+/g, '');
    if (!/^\d+(\.\d+)?$/.test(normalized)) {
        throw new Error(`parseAmountToMinor: cannot parse "${input}"`);
    }
    const [wholeRaw, fracRaw = ''] = normalized.split('.');
    const frac = (fracRaw + '0'.repeat(minorUnits)).slice(0, minorUnits);
    const rounded = fracRaw.length > minorUnits && Number(fracRaw[minorUnits]) >= 5 ? 1 : 0;
    const whole = wholeRaw ?? '0';
    return Number(whole) * 10 ** minorUnits + Number(frac || '0') + rounded;
}
//# sourceMappingURL=money.js.map